import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag,
  Drawer, Form, Input, message, Row, Col, DatePicker, Tabs, Progress,
} from 'antd'
import { CompassOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

const PATROL_STATUS_COLORS: Record<string, string> = {
  PLANNED: 'default',
  IN_PROGRESS: 'processing',
  COMPLETED: 'success',
  MISSED: 'error',
}

const SHIFTS = ['A', 'B', 'C']

export default function PatrolPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [dateFilter, setDateFilter] = useState<string>(dayjs().format('YYYY-MM-DD'))
  const [activeTab, setActiveTab] = useState('logs')
  const [logDrawerOpen, setLogDrawerOpen] = useState(false)
  const [routeDrawerOpen, setRouteDrawerOpen] = useState(false)
  const [logForm] = Form.useForm()
  const [routeForm] = Form.useForm()

  const { data: patrolLogs, isLoading: logsLoading } = useQuery({
    queryKey: ['patrol-logs', plantId, dateFilter],
    queryFn: () =>
      apiClient.get(`${API}/patrol-logs`, {
        params: { plant_id: plantId, date: dateFilter || undefined },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: patrolRoutes, isLoading: routesLoading } = useQuery({
    queryKey: ['patrol-routes', plantId],
    queryFn: () =>
      apiClient.get(`${API}/patrol-routes`, { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const createLogMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`${API}/patrol-logs`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patrol-logs'] })
      message.success('Patrol log created')
      setLogDrawerOpen(false)
      logForm.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const completeMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`${API}/patrol-logs/${id}/complete`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patrol-logs'] })
      message.success('Patrol marked complete')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const createRouteMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`${API}/patrol-routes`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patrol-routes'] })
      message.success('Route created')
      setRouteDrawerOpen(false)
      routeForm.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const logs = Array.isArray(patrolLogs) ? patrolLogs : []
  const routes = Array.isArray(patrolRoutes) ? patrolRoutes : []

  const checkpointColumns = [
    { title: 'Checkpoint', dataIndex: 'checkpoint_name', key: 'checkpoint_name' },
    {
      title: 'Scanned At',
      dataIndex: 'scanned_at',
      key: 'scanned_at',
      width: 100,
      render: (v: string) => v ? dayjs(v).format('HH:mm') : '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: string) => (
        <Tag color={v === 'SCANNED' ? 'success' : v === 'MISSED' ? 'error' : 'default'}>{v || 'PENDING'}</Tag>
      ),
    },
    { title: 'Note', dataIndex: 'exception_note', key: 'exception_note', ellipsis: true },
  ]

  const logColumns = [
    {
      title: 'Patrol No',
      dataIndex: 'patrol_no',
      key: 'patrol_no',
      width: 120,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>,
    },
    { title: 'Guard', dataIndex: 'guard_name', key: 'guard_name', width: 140 },
    { title: 'Shift', dataIndex: 'shift', key: 'shift', width: 70 },
    {
      title: 'Route',
      key: 'route',
      width: 140,
      render: (_: unknown, r: any) => r.route?.route_name || r.route_id || '—',
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 120,
      render: (v: string) => v ? dayjs(v).format('HH:mm') : '—',
    },
    {
      title: 'Completion',
      dataIndex: 'completion_pct',
      key: 'completion_pct',
      width: 130,
      render: (v: number) => <Progress percent={v ?? 0} size="small" />,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v: string) => (
        <Tag color={PATROL_STATUS_COLORS[v] ?? 'default'}>{v?.replace('_', ' ')}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: unknown, r: any) =>
        r.status === 'IN_PROGRESS' ? (
          <Button
            size="small"
            type="primary"
            loading={completeMut.isPending}
            onClick={() => completeMut.mutate(r.id)}
          >
            Mark Complete
          </Button>
        ) : null,
    },
  ]

  const routeColumns = [
    { title: 'Code', dataIndex: 'route_code', key: 'route_code', width: 120 },
    { title: 'Route Name', dataIndex: 'route_name', key: 'route_name' },
    {
      title: 'Checkpoints',
      dataIndex: 'checkpoints',
      key: 'checkpoints',
      render: (v: any) =>
        Array.isArray(v) ? v.join(', ') : (v || '—'),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <CompassOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Patrol Logs
      </Title>
      <Typography.Text type="secondary">Patrol logs and checkpoint tracking</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'logs', label: 'Patrol Logs' },
            { key: 'routes', label: 'Patrol Routes' },
          ]}
        />

        {activeTab === 'logs' && (
          <>
            <Row gutter={12} style={{ marginBottom: 16 }} justify="space-between">
              <Col>
                <Space wrap>
                  <Select
                    value={plantId}
                    onChange={setPlantId}
                    style={{ width: 200 }}
                    options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
                  />
                  <DatePicker
                    value={dateFilter ? dayjs(dateFilter) : null}
                    onChange={d => setDateFilter(d ? d.format('YYYY-MM-DD') : '')}
                    allowClear
                  />
                </Space>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => { logForm.resetFields(); setLogDrawerOpen(true) }}
                >
                  Add Patrol Log
                </Button>
              </Col>
            </Row>
            <Table
              dataSource={logs}
              columns={logColumns}
              rowKey="id"
              loading={logsLoading}
              size="small"
              pagination={{ pageSize: 50 }}
              scroll={{ x: 900 }}
              expandable={{
                expandedRowRender: (record: any) => {
                  const checkpoints = Array.isArray(record.checkpoints) ? record.checkpoints : []
                  return (
                    <Table
                      dataSource={checkpoints}
                      columns={checkpointColumns}
                      rowKey={(r: any) => r.id ?? r.checkpoint_name}
                      size="small"
                      pagination={false}
                    />
                  )
                },
              }}
            />
          </>
        )}

        {activeTab === 'routes' && (
          <>
            <Row justify="end" style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => { routeForm.resetFields(); setRouteDrawerOpen(true) }}
              >
                Add Route
              </Button>
            </Row>
            <Table
              dataSource={routes}
              columns={routeColumns}
              rowKey="id"
              loading={routesLoading}
              size="small"
              pagination={{ pageSize: 50 }}
            />
          </>
        )}
      </Card>

      {/* Patrol Log Drawer */}
      <Drawer
        title="Add Patrol Log"
        open={logDrawerOpen}
        onClose={() => { setLogDrawerOpen(false); logForm.resetFields() }}
        width={440}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setLogDrawerOpen(false); logForm.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createLogMut.isPending}
                onClick={() =>
                  logForm.validateFields().then(vals =>
                    createLogMut.mutate({
                      ...vals,
                      plant_id: plantId,
                      patrol_date: vals.patrol_date
                        ? vals.patrol_date.format('YYYY-MM-DD')
                        : undefined,
                    })
                  )
                }
              >
                Create
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={logForm} layout="vertical">
          <Form.Item name="route_id" label="Patrol Route" rules={[{ required: true }]}>
            <Select
              options={routes.map((r: any) => ({ value: r.id, label: r.route_name }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="guard_name" label="Guard Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="shift" label="Shift" rules={[{ required: true }]}>
            <Select options={SHIFTS.map(s => ({ value: s, label: `Shift ${s}` }))} />
          </Form.Item>
          <Form.Item name="patrol_date" label="Patrol Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Route Drawer */}
      <Drawer
        title="Add Patrol Route"
        open={routeDrawerOpen}
        onClose={() => { setRouteDrawerOpen(false); routeForm.resetFields() }}
        width={440}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setRouteDrawerOpen(false); routeForm.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createRouteMut.isPending}
                onClick={() =>
                  routeForm.validateFields().then(vals => {
                    const checkpoints = vals.checkpoints
                      ? vals.checkpoints.split(',').map((s: string) => s.trim()).filter(Boolean)
                      : []
                    createRouteMut.mutate({ ...vals, plant_id: plantId, checkpoints })
                  })
                }
              >
                Create
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={routeForm} layout="vertical">
          <Form.Item name="route_code" label="Route Code" rules={[{ required: true }]}>
            <Input placeholder="e.g. RT-001" />
          </Form.Item>
          <Form.Item name="route_name" label="Route Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="checkpoints" label="Checkpoints (comma-separated)">
            <Input placeholder="Gate A, Godown 1, Spinning Hall, Canteen" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
